import React, { useState, useMemo } from 'react';
import './LowEngagementUsersList.css';

/**
 * LowEngagementUsersList - Displays users with engagement score < 0.3
 * 
 * Features:
 * - Paginated table (50 users per page)
 * - Sortable by engagement score or last login date
 * - Contact action button for each user
 * - Empty state handling
 * - Loading state
 * 
 * Props:
 * - users: Array of { userId, email, lastLogin, engagementScore }
 * - loading: boolean
 * - onContactUser: (userId, email) => void
 */
const LowEngagementUsersList = ({ users = [], loading = false, onContactUser }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('engagementScore'); // 'engagementScore' | 'lastLogin'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' | 'desc'
  const itemsPerPage = 50;

  // Sort users
  const sortedUsers = useMemo(() => {
    if (!users || users.length === 0) return [];

    const sorted = [...users].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === 'engagementScore') {
        aValue = a.engagementScore || 0;
        bValue = b.engagementScore || 0;
      } else if (sortBy === 'lastLogin') {
        aValue = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        bValue = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
      }

      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  }, [users, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = sortedUsers.slice(startIndex, endIndex);

  // Handle sort change
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change sort column
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle contact user
  const handleContact = (userId, email) => {
    if (onContactUser) {
      onContactUser(userId, email);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format engagement score
  const formatScore = (score) => {
    if (score === null || score === undefined) return 'N/A';
    return score.toFixed(2);
  };

  // Get score color class
  const getScoreColorClass = (score) => {
    if (score === null || score === undefined) return '';
    if (score < 0.1) return 'low-engagement-users-list__score--critical';
    if (score < 0.2) return 'low-engagement-users-list__score--warning';
    return 'low-engagement-users-list__score--low';
  };

  // Render loading state
  if (loading) {
    return (
      <div className="low-engagement-users-list">
        <div className="low-engagement-users-list__header">
          <h3 className="low-engagement-users-list__title">Low Engagement Users</h3>
          <p className="low-engagement-users-list__subtitle">Users with engagement score &lt; 0.3</p>
        </div>
        <div className="low-engagement-users-list__loading">
          <div className="low-engagement-users-list__spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!users || users.length === 0) {
    return (
      <div className="low-engagement-users-list">
        <div className="low-engagement-users-list__header">
          <h3 className="low-engagement-users-list__title">Low Engagement Users</h3>
          <p className="low-engagement-users-list__subtitle">Users with engagement score &lt; 0.3</p>
        </div>
        <div className="low-engagement-users-list__empty">
          <p>No low engagement users found.</p>
          <p className="low-engagement-users-list__empty-hint">
            This is great! All users have engagement scores above 0.3.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="low-engagement-users-list">
      <div className="low-engagement-users-list__header">
        <h3 className="low-engagement-users-list__title">Low Engagement Users</h3>
        <p className="low-engagement-users-list__subtitle">
          {sortedUsers.length} user{sortedUsers.length !== 1 ? 's' : ''} with engagement score &lt; 0.3
        </p>
      </div>

      <div className="low-engagement-users-list__table-container">
        <table className="low-engagement-users-list__table">
          <thead>
            <tr>
              <th className="low-engagement-users-list__th">
                <button
                  className={`low-engagement-users-list__sort-button ${
                    sortBy === 'email' ? 'low-engagement-users-list__sort-button--active' : ''
                  }`}
                  onClick={() => handleSort('email')}
                  aria-label="Sort by email"
                >
                  Email
                  {sortBy === 'email' && (
                    <span className="low-engagement-users-list__sort-icon">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th className="low-engagement-users-list__th">
                <button
                  className={`low-engagement-users-list__sort-button ${
                    sortBy === 'lastLogin' ? 'low-engagement-users-list__sort-button--active' : ''
                  }`}
                  onClick={() => handleSort('lastLogin')}
                  aria-label="Sort by last login date"
                >
                  Last Login
                  {sortBy === 'lastLogin' && (
                    <span className="low-engagement-users-list__sort-icon">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th className="low-engagement-users-list__th">
                <button
                  className={`low-engagement-users-list__sort-button ${
                    sortBy === 'engagementScore' ? 'low-engagement-users-list__sort-button--active' : ''
                  }`}
                  onClick={() => handleSort('engagementScore')}
                  aria-label="Sort by engagement score"
                >
                  Engagement Score
                  {sortBy === 'engagementScore' && (
                    <span className="low-engagement-users-list__sort-icon">
                      {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </button>
              </th>
              <th className="low-engagement-users-list__th low-engagement-users-list__th--actions">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user) => (
              <tr key={user.userId} className="low-engagement-users-list__row">
                <td className="low-engagement-users-list__td low-engagement-users-list__td--email">
                  {user.email}
                </td>
                <td className="low-engagement-users-list__td">
                  {formatDate(user.lastLogin)}
                </td>
                <td className="low-engagement-users-list__td">
                  <span className={`low-engagement-users-list__score ${getScoreColorClass(user.engagementScore)}`}>
                    {formatScore(user.engagementScore)}
                  </span>
                </td>
                <td className="low-engagement-users-list__td low-engagement-users-list__td--actions">
                  <button
                    className="low-engagement-users-list__contact-button"
                    onClick={() => handleContact(user.userId, user.email)}
                    aria-label={`Contact ${user.email}`}
                  >
                    Contact
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="low-engagement-users-list__pagination">
          <button
            className="low-engagement-users-list__pagination-button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            Previous
          </button>

          <div className="low-engagement-users-list__pagination-info">
            Page {currentPage} of {totalPages}
            <span className="low-engagement-users-list__pagination-range">
              ({startIndex + 1}-{Math.min(endIndex, sortedUsers.length)} of {sortedUsers.length})
            </span>
          </div>

          <button
            className="low-engagement-users-list__pagination-button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default LowEngagementUsersList;
